import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';

interface OrderState {
  orderNumber: string;
  total: number;
  orderType: string;
}

export default function OrderConfirmation() {
  const location = useLocation();
  const order = (location.state as { order?: OrderState } | null)?.order;

  return (
    <>
      <Helmet>
        <title>Order Confirmed — Biryani2Go</title>
      </Helmet>

      <section className="bg-surface min-h-[70vh] flex items-center py-20">
        <div className="max-w-screen-md mx-auto px-5 md:px-8 text-center w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, type: 'spring', bounce: 0.35 }}
            className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle2 size={40} className="text-emerald-600" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-display font-semibold text-3xl md:text-5xl text-maroon mb-4"
          >
            Your dabba is on its way to the kitchen.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.18 }}
            className="text-lg text-ink-2 mb-8"
          >
            Thank you for your order! We'll start sealing and steaming right away.
          </motion.p>

          {order && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.26 }}
              className="card-tin p-8 mb-8 text-left max-w-md mx-auto"
            >
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2 text-sm">Order Number</span>
                <span className="font-semibold text-maroon">{order.orderNumber}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-2 text-sm">Order Type</span>
                <span className="font-semibold text-maroon capitalize">{order.orderType}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-ink-2 text-sm">Total Paid</span>
                <span className="font-semibold text-ember tabular-nums">${order.total?.toFixed(2)}</span>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.34 }}
            className="flex flex-wrap justify-center gap-4"
          >
            {order?.orderNumber && (
              <Link to={`/order-status?order=${order.orderNumber}`} className="btn-primary">
                Track Order <ArrowRight size={18} />
              </Link>
            )}
            <Link to="/menu" className="btn-secondary">
              Order More
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
